import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import MainHeader from "../components/MainHeader";
import MainFooter from "../components/MainFooter";
import { useFilmmakerScope } from "../context/FilmmakerScope";
import { Trophy, Ticket, TrendingUp } from "lucide-react";

export default function BoxOffice() {
  const [data, setData] = useState({ films: [], total_gross: 0, total_tickets: 0, filmmaker: null });
  const [loading, setLoading] = useState(true);
  const scope = useFilmmakerScope();

  useEffect(() => {
    document.body.classList.remove("theme-mini");
    if (scope.loading) return;
    setLoading(true);
    const url = scope.isFilmmakerMode && scope.slug ? `/box-office?filmmaker=${scope.slug}` : "/box-office";
    api.get(url).then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [scope.loading, scope.isFilmmakerMode, scope.slug]);

  const top = data.films[0];
  const inScope = scope.isFilmmakerMode && scope.filmmaker;

  return (
    <div data-testid="box-office-page" className="min-h-screen bg-gs-cream">
      <MainHeader />
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-12">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gs-ember">The numbers</p>
        <h1 className="font-serif text-5xl md:text-6xl text-gs-ink mt-3 leading-[1.05]">
          {inScope ? `${scope.filmmaker.name}'s Box Office` : "Box Office"}
        </h1>
        <p className="font-sans text-lg text-gs-ink/70 mt-4 max-w-2xl">
          {inScope
            ? `Real-time receipts for the films of ${scope.filmmaker.name}. Their share of the Green Screen theater.`
            : "Real-time receipts from the Green Screen theater. Every rental, every sale, every film — out in the open."}
        </p>

        <div className="grid md:grid-cols-3 gap-5 mt-12">
          <Stat icon={TrendingUp} label={inScope ? "Filmmaker gross" : "Total gross"} value={`$${data.total_gross.toFixed(2)}`} />
          <Stat icon={Ticket} label="Tickets sold" value={data.total_tickets.toString()} />
          <Stat icon={Trophy} label={inScope ? "Top film" : "Top performer"} value={top ? top.title : "—"} sub={top ? `$${top.gross.toFixed(2)}` : ""} />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <div className="bg-gs-paper border border-gs-line rounded-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gs-line bg-gs-cream/50">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gs-dust">
              {inScope ? "Per-film breakdown · scoped" : "Per-film breakdown"}
            </p>
          </div>
          {loading ? (
            <p className="p-8 font-mono text-xs uppercase tracking-widest text-gs-dust">Loading receipts…</p>
          ) : data.films.length === 0 ? (
            <p className="p-12 font-serif text-xl italic text-gs-ink/60 text-center">
              {inScope ? "No tickets sold for this filmmaker yet." : "No tickets sold yet. The marquee is fresh."}
            </p>
          ) : (
            <div className="divide-y divide-gs-line">
              {data.films.map((f, i) => (
                <Link
                  key={f.slug}
                  to={`/film/${f.slug}`}
                  data-testid={`box-office-row-${i}`}
                  className="grid grid-cols-12 gap-4 items-center px-6 py-4 hover:bg-gs-cream/60 transition-colors"
                >
                  <div className="col-span-1 font-display text-3xl text-gs-ember">{String(i + 1).padStart(2, "0")}</div>
                  <div className="col-span-2 aspect-[3/2] overflow-hidden rounded-sm bg-gs-ink">
                    {f.poster_url && <img src={f.poster_url} alt={f.title} className="w-full h-full object-cover" />}
                  </div>
                  <div className="col-span-4">
                    <p className="font-outfit font-semibold text-xl text-gs-ink">{f.title}</p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-gs-dust mt-1">
                      {f.year} · {f.runtime_minutes}m · {f.rating}
                    </p>
                  </div>
                  <div className="col-span-2 font-mono text-xs">
                    <p className="text-gs-dust uppercase tracking-widest text-[10px]">Tickets</p>
                    <p className="text-gs-ink text-base mt-1">{f.tickets}</p>
                    <p className="text-gs-dust text-[10px] mt-0.5">{f.sales} buys · {f.rentals} rentals</p>
                  </div>
                  <div className="col-span-3 text-right">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-gs-dust">Gross</p>
                    <p className="font-display text-3xl text-gs-ember mt-1">${f.gross.toFixed(2)}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
      <MainFooter />
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-gs-paper border border-gs-line rounded-md p-6">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-widest text-gs-dust">{label}</p>
        <Icon size={18} className="text-gs-ember" />
      </div>
      <p className="font-display text-5xl text-gs-ink mt-4 tracking-wide">{value}</p>
      {sub && <p className="font-mono text-[10px] uppercase tracking-widest text-gs-ember mt-2">{sub}</p>}
    </div>
  );
}
